import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Newspaper, CalendarDays, Images, Users, HandCoins, Settings, LogOut, Languages, Cake, Mail, Bell, HandHelping, ShieldUser, ClipboardList, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { useI18n } from "@/contexts/I18nContext";

const nav = [
  { to: "/admin", end: true, key: "dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/news", key: "news", icon: Newspaper, testid: "admin-nav-news" },
  { to: "/admin/events", key: "events", icon: CalendarDays, testid: "admin-nav-events" },
  { to: "/admin/photos", key: "photos", icon: Images, testid: "admin-nav-photos" },
  { to: "/admin/members", key: "members", icon: Users, testid: "admin-nav-members" },
  { to: "/admin/celebrations", key: "celebrations", icon: Cake, testid: "admin-nav-celebrations" },
  { to: "/admin/donations", key: "donations", icon: HandCoins, testid: "admin-nav-donations" },
  { to: "/admin/prayers", key: "prayers", icon: HandHelping, testid: "admin-nav-prayers" },
  { to: "/admin/team", key: "team", icon: HeartHandshake, testid: "admin-nav-team" },
  { to: "/admin/notifications", key: "notifications", icon: Bell, testid: "admin-nav-notifications" },
  { to: "/admin/survey", key: "survey", icon: ClipboardList, testid: "admin-nav-survey" },
  { to: "/admin/email", key: "email", icon: Mail, testid: "admin-nav-email" },
  { to: "/admin/accounts", key: "accounts", icon: ShieldUser, testid: "admin-nav-accounts" },
  { to: "/admin/settings", key: "settings", icon: Settings, testid: "admin-nav-settings" },
];

export function AdminSidebar() {
  const { signOut } = useAdminAuth();
  const { t, lang, toggle } = useI18n();
  const navigate = useNavigate();

  const handleLogout = () => {
    signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-60 shrink-0 flex-col gap-2 border-r border-border bg-card/40 py-6 pr-3" data-testid="admin-sidebar">
        <Link to="/admin" className="mb-2 flex items-center gap-3 px-2">
          <Logo size={36} />
          <div className="leading-tight">
            <p className="font-serif-display text-sm font-semibold tracking-tight">Iglesia NCL</p>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.admin.title}</p>
          </div>
        </Link>
        <nav className="mt-2 flex-1 space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-primary/10 text-primary border-l-2 border-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {t.admin[item.key]}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-2 space-y-2 px-1">
          <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={toggle} data-testid="admin-language-toggle">
            <Languages className="h-4 w-4" />
            {lang === "es" ? "Espanol" : "English"}
          </Button>
          <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={handleLogout} data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" />
            {t.admin.logout}
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden -mx-3 sticky top-0 z-40 mb-3 flex items-center justify-between border-b border-border bg-background/95 backdrop-blur px-3 py-2">
        <Link to="/admin" className="flex items-center gap-2">
          <Logo size={32} />
          <p className="font-serif-display text-sm font-semibold">{t.admin.title}</p>
        </Link>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={toggle} className="gap-1" data-testid="admin-language-toggle-mobile">
            <Languages className="h-4 w-4" />
            <span className="text-xs">{lang.toUpperCase()}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-button-mobile">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="md:hidden -mx-3 mb-3 flex gap-1 overflow-x-auto px-3 pb-2" data-testid="admin-mobile-nav">
        {nav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.end}
              data-testid={`${item.testid}-mobile`}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
                  isActive ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground bg-white"
                }`
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {t.admin[item.key]}
            </NavLink>
          );
        })}
      </div>
    </>
  );
}
